import React, { useEffect, useMemo, useState } from "react"
import {
  METRIC_CATALOG,
  calcIdsReferencing,
  calculatedMetricCatalogEntries,
  type CalcExpr,
  type CalcFormat,
  type CalcOperator,
  type CalculatedMetricDef,
  type MetricCatalogEntry,
} from "~/shared/kpi"
import Input from "~/ui-rtk/components/ui/controls/Input"
import {
  metricEntryFor,
  useMetricDictionary,
} from "~/ui-rtk/hooks/metric-dictionary"
import Select, {
  type TSelectOption,
} from "~/ui-rtk/components/ui/controls/Select"
import Chip from "~/ui-rtk/components/ui/common/Chip"
import { cn } from "~/ui-rtk/utils/tailwind-utils"

// Builder token stream (ephemeral UI state). It is parsed into the stored
// CalcExpr AST on Save via `tokensToCalcExpr` — the report never stores the
// token list, only the normalized AST.
type BuilderToken =
  | { t: "metric"; key: string; label: string }
  | { t: "const"; value: number }
  | { t: "op"; op: CalcOperator }
  | { t: "lparen" }
  | { t: "rparen" }

interface CalculatedMetricBuilderModalProps {
  isOpen: boolean
  /** The metric being edited, or null to create a new one. */
  metric: CalculatedMetricDef | null
  /** Company custom metrics offered as additional operands (custom.* source). */
  customEntries?: MetricCatalogEntry[]
  /** Synthesized CUSTOM_* initiative-type breakout entries, offered as operands. */
  initiativeEntries?: MetricCatalogEntry[]
  /**
   * All calculated metrics of the report's working copy. Offered as nested
   * `calc.<id>` operands, minus the metric being edited and any metric whose
   * formula (transitively) references it — using those would create a
   * reference cycle.
   */
  calculatedMetrics?: CalculatedMetricDef[]
  onClose: () => void
  onSave: (metric: CalculatedMetricDef) => void
}

const FORMAT_OPTIONS: TSelectOption[] = [
  { label: "Currency ($)", value: "currency" },
  { label: "Number", value: "number" },
  { label: "Percent (%)", value: "percent" },
  { label: "Decimal", value: "decimal" },
]

const OP_SYMBOL: Record<CalcOperator, string> = {
  "+": "+",
  "-": "−",
  "*": "×",
  "/": "÷",
}

const OP_PRECEDENCE: Record<CalcOperator, number> = {
  "+": 1,
  "-": 1,
  "*": 2,
  "/": 2,
}

// Operand universe: the ENTIRE frozen catalog — every cube source the custom
// reports can render (metrics, brand_value, media_campaigns, initiatives,
// computed) is a valid operand; the evaluator joins non-main datasets by
// bucket date. Company custom.* metrics, CUSTOM_* initiative breakouts, and
// the report's other calc.* metrics are appended per-render in the component —
// never baked into this module-level base (they are company/report-scoped and
// async).
const BASE_OPERAND_CATALOG = METRIC_CATALOG

// Shunting-yard: parse an infix token stream into a CalcExpr AST honoring
// precedence (× ÷ over + −) and parentheses. Returns null for any malformed
// input (leftover operands, unbalanced parens, missing operands).
function tokensToCalcExpr(tokens: BuilderToken[]): CalcExpr | null {
  if (tokens.length === 0) return null
  const output: CalcExpr[] = []
  const ops: Array<CalcOperator | "("> = []

  const apply = (op: CalcOperator): boolean => {
    const right = output.pop()
    const left = output.pop()
    if (!left || !right) return false
    output.push({ kind: "op", op, left, right })
    return true
  }

  for (const token of tokens) {
    if (token.t === "metric") {
      output.push({ kind: "ref", key: token.key })
    } else if (token.t === "const") {
      output.push({ kind: "const", value: token.value })
    } else if (token.t === "op") {
      while (
        ops.length > 0 &&
        ops[ops.length - 1] !== "(" &&
        OP_PRECEDENCE[ops[ops.length - 1] as CalcOperator] >=
          OP_PRECEDENCE[token.op]
      ) {
        if (!apply(ops.pop() as CalcOperator)) return null
      }
      ops.push(token.op)
    } else if (token.t === "lparen") {
      ops.push("(")
    } else {
      // rparen
      while (ops.length > 0 && ops[ops.length - 1] !== "(") {
        if (!apply(ops.pop() as CalcOperator)) return null
      }
      if (ops.pop() !== "(") return null // unbalanced
    }
  }
  while (ops.length > 0) {
    const op = ops.pop()
    if (op === "(") return null // unbalanced
    if (!apply(op as CalcOperator)) return null
  }
  return output.length === 1 ? output[0] : null
}

function makeId(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `calc-${Date.now()}`
}

function tokenLabel(token: BuilderToken): string {
  switch (token.t) {
    case "metric":
      return token.label
    case "const":
      return String(token.value)
    case "op":
      return OP_SYMBOL[token.op]
    case "lparen":
      return "("
    case "rparen":
      return ")"
  }
}

function tokenChipClass(token: BuilderToken): string {
  if (token.t === "metric") return "bg-system-blue-600 text-white"
  if (token.t === "const") return "bg-system-blue-700 text-white"
  return "bg-system-blue-950 text-system-blue-100"
}

// Flattens a CalcExpr AST back into a builder token strip so an existing metric
// can be edited. Parenthesizes nested op nodes to preserve grouping; the result
// re-parses to an equivalent AST. Operand labels resolve from the passed
// catalog map (custom operands included).
function exprToTokens(
  expr: CalcExpr,
  catalogByKey: Map<string, MetricCatalogEntry>,
): BuilderToken[] {
  const walk = (node: CalcExpr, wrap: boolean): BuilderToken[] => {
    if (node.kind === "const") return [{ t: "const", value: node.value }]
    if (node.kind === "ref") {
      return [
        {
          t: "metric",
          key: node.key,
          label: catalogByKey.get(node.key)?.label ?? node.key,
        },
      ]
    }
    const inner: BuilderToken[] = [
      ...walk(node.left, true),
      { t: "op", op: node.op },
      ...walk(node.right, true),
    ]
    return wrap ? [{ t: "lparen" }, ...inner, { t: "rparen" }] : inner
  }
  return walk(expr, false)
}

// Structured formula builder for a user-defined calculated metric. Users add
// metric operands, numeric constants, operators (+ − × ÷), and parentheses; the
// token stream is parsed to a CalcExpr AST and handed back via onSave. The
// parent (ReportEditorModal) owns persistence.
const CalculatedMetricBuilderModal: React.FC<
  CalculatedMetricBuilderModalProps
> = ({
  isOpen,
  metric,
  customEntries,
  initiativeEntries,
  calculatedMetrics,
  onClose,
  onSave,
}) => {
  const [name, setName] = useState("")
  const [format, setFormat] = useState<CalcFormat>("number")
  const [tokens, setTokens] = useState<BuilderToken[]>([])
  const [constInput, setConstInput] = useState("")
  const [operandSearch, setOperandSearch] = useState("")

  // The report's other calc metrics as nested operands. When editing, exclude
  // the metric itself and every metric whose formula reaches it — picking one
  // of those would create a reference cycle (which evaluates to null).
  const calcOperandEntries = useMemo(() => {
    const defs = calculatedMetrics ?? []
    if (!metric) return calculatedMetricCatalogEntries(defs)
    const cyclic = calcIdsReferencing(metric.id, defs)
    return calculatedMetricCatalogEntries(
      defs.filter(def => def.id !== metric.id && !cyclic.has(def.id)),
    )
  }, [calculatedMetrics, metric])

  // Operand labels come from the Metric Dictionary (single source of truth),
  // matching the KPI metric picker/table; catalog label is the fallback
  // (custom/calc operands aren't catalogued). catalogByKey (below) inherits
  // these labels.
  const { metricMap } = useMetricDictionary()
  const operandCatalog = useMemo(
    () =>
      [
        ...BASE_OPERAND_CATALOG,
        ...(customEntries ?? []),
        ...(initiativeEntries ?? []),
        ...calcOperandEntries,
      ].map(entry => {
        const title = metricEntryFor(metricMap, entry.key)?.title
        return title && title !== entry.label
          ? { ...entry, label: title }
          : entry
      }),
    [customEntries, initiativeEntries, calcOperandEntries, metricMap],
  )
  const catalogByKey = useMemo(
    () => new Map(operandCatalog.map(entry => [entry.key, entry])),
    [operandCatalog],
  )

  // Reseed when a different metric (or create) is opened. Editing seeds the name
  // + format; the expression is re-derived into a flat token strip when possible
  // (a nested AST that can't be flattened losslessly still opens for name/format
  // edits, with the expression shown read-only-ish — but our builder always
  // produces flat-parseable ASTs, so round-tripping via a fresh strip is fine).
  useEffect(() => {
    if (!isOpen) return
    setName(metric?.name ?? "")
    setFormat(metric?.format ?? "number")
    setTokens(metric ? exprToTokens(metric.expression, catalogByKey) : [])
    setConstInput("")
    setOperandSearch("")
  }, [isOpen, metric, catalogByKey])

  const expr = useMemo(() => tokensToCalcExpr(tokens), [tokens])
  const isValid = name.trim().length > 0 && expr !== null

  const operandResults = useMemo(() => {
    const query = operandSearch.trim().toLowerCase()
    if (!query) return operandCatalog.slice(0, 50)
    return operandCatalog
      .filter(
        entry =>
          entry.label.toLowerCase().includes(query) ||
          entry.category.toLowerCase().includes(query),
      )
      .slice(0, 50)
  }, [operandSearch, operandCatalog])

  const appendToken = (token: BuilderToken) =>
    setTokens(list => [...list, token])
  const removeTokenAt = (index: number) =>
    setTokens(list => list.filter((_, i) => i !== index))
  const popToken = () => setTokens(list => list.slice(0, -1))

  const handleAddConst = () => {
    const value = Number(constInput)
    if (constInput.trim() === "" || !Number.isFinite(value)) return
    appendToken({ t: "const", value })
    setConstInput("")
  }

  const handleSave = () => {
    if (!isValid || !expr) return
    onSave({
      id: metric?.id ?? makeId(),
      name: name.trim(),
      format,
      expression: expr,
    })
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-system-blue-900 border border-system-blue-700 rounded-system-lg shadow-2xl w-full max-w-2xl mx-4 max-h-[90vh] flex flex-col">
        <div className="p-6 flex items-center justify-between border-b border-system-blue-700">
          <h2 className="text-xl font-semibold text-white">
            {metric ? "Edit calculated metric" : "New calculated metric"}
          </h2>
          <button
            onClick={onClose}
            className="text-system-blue-300 hover:text-white transition-colors"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div className="p-6 flex flex-col gap-4 overflow-y-auto">
          <div className="flex items-end gap-4">
            <Input
              fullWidth
              label="Metric name"
              value={name}
              onChange={event => setName(event.target.value)}
              placeholder="e.g. Amazon net of ad spend"
              classes={{ input: "text-white" }}
            />
            <div className="w-48">
              <label className="block text-sm text-system-blue-200 mb-1">
                Format
              </label>
              <Select
                options={FORMAT_OPTIONS}
                value={format}
                onChange={event => setFormat(event.target.value as CalcFormat)}
              />
            </div>
          </div>
          <p className="text-system-blue-300 text-xs -mt-2">
            Format is display-only — “Percent” just appends “%”. For a ratio as
            a percentage, multiply by 100 in the formula (e.g. a ÷ b × 100).
          </p>

          {/* Expression strip */}
          <div>
            <p className="text-white text-sm font-semibold mb-2">Formula</p>
            <div className="min-h-[3rem] flex flex-wrap items-center gap-2 p-3 bg-system-blue-800 border border-system-blue-600 rounded-system-md">
              {tokens.length === 0 ? (
                <span className="text-system-blue-300 text-sm">
                  Build a formula by adding metrics, numbers, and operators
                  below.
                </span>
              ) : (
                tokens.map((token, index) => (
                  <Chip
                    key={index}
                    className={cn("text-sm", tokenChipClass(token))}
                    onRemove={() => removeTokenAt(index)}
                  >
                    {tokenLabel(token)}
                  </Chip>
                ))
              )}
            </div>
            <div className="mt-1 h-4">
              {tokens.length > 0 && !expr && (
                <span className="text-system-error-400 text-xs">
                  Incomplete or invalid formula.
                </span>
              )}
            </div>
          </div>

          {/* Operator + constant controls */}
          <div className="flex flex-wrap items-center gap-2">
            {(["+", "-", "*", "/"] as CalcOperator[]).map(op => (
              <button
                key={op}
                type="button"
                onClick={() => appendToken({ t: "op", op })}
                className="w-9 h-9 flex items-center justify-center bg-system-blue-700 hover:bg-system-blue-600 text-white rounded-system-md text-lg"
              >
                {OP_SYMBOL[op]}
              </button>
            ))}
            <button
              type="button"
              onClick={() => appendToken({ t: "lparen" })}
              className="w-9 h-9 flex items-center justify-center bg-system-blue-700 hover:bg-system-blue-600 text-white rounded-system-md"
            >
              (
            </button>
            <button
              type="button"
              onClick={() => appendToken({ t: "rparen" })}
              className="w-9 h-9 flex items-center justify-center bg-system-blue-700 hover:bg-system-blue-600 text-white rounded-system-md"
            >
              )
            </button>
            <div className="flex items-center gap-1 ml-2">
              <input
                type="number"
                value={constInput}
                onChange={event => setConstInput(event.target.value)}
                onKeyDown={event => {
                  if (event.key === "Enter") {
                    event.preventDefault()
                    handleAddConst()
                  }
                }}
                placeholder="Number"
                className="input input-sm w-28 bg-basic-dark-blue border border-basic-blue text-white"
              />
              <button
                type="button"
                onClick={handleAddConst}
                className="px-3 h-9 bg-system-blue-700 hover:bg-system-blue-600 text-white rounded-system-md text-sm"
              >
                Add #
              </button>
            </div>
            <button
              type="button"
              onClick={popToken}
              disabled={tokens.length === 0}
              className="px-3 h-9 bg-system-blue-800 hover:bg-system-blue-700 text-system-blue-200 rounded-system-md text-sm disabled:opacity-40 ml-auto"
            >
              Backspace
            </button>
            <button
              type="button"
              onClick={() => setTokens([])}
              disabled={tokens.length === 0}
              className="px-3 h-9 bg-system-blue-800 hover:bg-system-blue-700 text-system-blue-200 rounded-system-md text-sm disabled:opacity-40"
            >
              Clear
            </button>
          </div>

          {/* Operand (metric) picker */}
          <div className="flex flex-col overflow-hidden">
            <p className="text-white text-sm font-semibold mb-2">
              Add a metric
            </p>
            <Input
              fullWidth
              placeholder="Search metrics…"
              value={operandSearch}
              onChange={event => setOperandSearch(event.target.value)}
              classes={{ input: "text-white" }}
            />
            <div className="mt-2 max-h-40 overflow-y-auto space-y-1 pr-1">
              {operandResults.length === 0 && (
                <p className="text-system-blue-300 text-sm">
                  No metrics match.
                </p>
              )}
              {operandResults.map(entry => (
                <button
                  key={entry.key}
                  type="button"
                  onClick={() =>
                    appendToken({
                      t: "metric",
                      key: entry.key,
                      label: entry.label,
                    })
                  }
                  className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-system-md text-left text-sm bg-system-blue-800 text-white hover:bg-system-blue-700"
                >
                  <span className="truncate">{entry.label}</span>
                  <span className="text-xs text-system-blue-300 flex-shrink-0">
                    {entry.category}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="p-6 flex items-center justify-end gap-3 border-t border-system-blue-700">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-system-blue-300 hover:text-white border border-system-blue-600 hover:border-system-blue-500 rounded-system-md transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!isValid}
            className="px-4 py-2 text-sm text-white bg-system-brand-pink-500 hover:bg-system-brand-pink-600 rounded-system-md transition-colors disabled:opacity-50"
          >
            Save metric
          </button>
        </div>
      </div>
    </div>
  )
}

export default CalculatedMetricBuilderModal
